/**
 * English (United States) Messages
 * This file contains all messages for en_US locale
 */

export const ERROR_MESSAGES = {
    // Template related errors
    TEMPLATE: {
        FRIENDLY_NAME_REQUIRED: "Enter friendlyName",
        FRIENDLY_NAME_REQUIRED_ALT: "Enter the friendly name",
        MESSAGE_REQUIRED: "Template content is required.",
        MODULE_REQUIRED: "Module selection is required",
    },

    // Form validation errors
    VALIDATION: {
        REQUIRED: "Required",
        PHONE_NUMBER_REQUIRED: "Phone number is required.",
        PHONE_NUMBER_INVALID: "Please provide a valid phone number.",
        NAME_REQUIRED: "Please provide a name.",
        EMAIL_REQUIRED: "Email is required",
        EMAIL_INVALID: "Please provide a valid email address",
        ALL_FIELDS_REQUIRED: "Please fill all fields and check your email format",
        SAVED_VIEW_NAME_REQUIRED: "Enter a name to save this view.",
    },

    // Message related errors
    MESSAGE: {
        TO_NUMBER_REQUIRED: "To Number is required !",
        MESSAGE_BODY_REQUIRED: "Message Body is required !",
        SEND_FAILED: "Failed to send message",
        SEND_ERROR: "An error occurred while sending the message",
    },

    // General errors
    GENERAL: {
        SOMETHING_WENT_WRONG: "Something went wrong !",
        ERROR: "Error",
        UNKNOWN_ERROR: "An unexpected error occurred",
    },

    // Phone/Number related errors
    PHONE: {
        NUMBER_REQUIRED: "Please provide a phone number.",
        INVALID_FORMAT: "Please enter a valid phone number format",
    },

    // User/Admin related errors
    USER: {
        INVALID_CREDENTIALS: "Invalid credentials",
        ACCESS_DENIED: "Access denied",
        USER_NOT_FOUND: "User not found",
        ADD_USER_ERROR: "Please select one user to continue.",
        ALREADY_PART_OF_TEAM: "User Already Part of Team",
    },
    CHANNELS: {},
    TEAMS: {
        SELECT_USER_REQUIRED: "Please select at least one user to create a team.",
        FRIENDLY_NAME_REQUIRED: "Please provide a Friendly name to create a team.",
    },
};

export const SUCCESS_MESSAGES = {
    // Template related success messages
    TEMPLATE: {
        ADDED: "Template added! Personalize client messages with ease.",
        UPDATED: "Template updated! Your message is refreshed.",
        AFTER_ADDING_MESSAGE: "Simplify your messaging with customizable templates for efficient communication.",
        DELETED: "Template deleted. Create a new one as needed.",
    },

    // Message related success messages
    MESSAGE: {
        SENT_SUCCESS: "Message sent successfully",
    },

    // General success messages
    GENERAL: {
        OPERATION_SUCCESS: "Operation completed successfully",
        SAVED: "Changes saved successfully",
        UPDATED: "Updated successfully",
        DELETED: "Deleted successfully",
    },

    // User/Admin related success messages
    USER: {
        CREATED: "User have been updated successfully.",
        UPDATED: "User have been updated successfully.",
        DELETED: "User deleted. Feel free to add a new one when needed.",
    },
    CHANNELS: {
        TWILIO_CONFIGURED: "Service connected successfully! Proceed to add a phone number.",
        TWILIO_NUMBER_ADDED: "Phone number added. You can now send Message to clients",
    },
    TEAMS: {
        CREATED: "Team Created Successfully.",
        DELETED: "Team deleted Successfully.",
    },
};

export const CONFIRMATION_MESSAGES = {
    // Template related confirmation messages
    TEMPLATE: {
        DELETE_CONFIRMATION: "Delete this template? It might still be used in automations or campaigns.",
    },

    // General confirmation messages
    GENERAL: {
        DELETE_CONFIRMATION: "Sure you want to Delete ?",
        SAVE_CONFIRMATION: "Do you want to save your changes?",
        CANCEL_CONFIRMATION: "Are you sure you want to cancel? Your changes will be lost.",
    },
};

export const INFO_MESSAGES = {
    // Template related informational messages
    TEMPLATE: {
        INFO: "This template contains informational details.",
    },

    INBOX: {
        STAY_CONNECTED: "Stay connected",
        STAY_CONNECTED_TO_INITIATE_NEW_MESSAGE:
            "Begin typing a contact name or number above to initiate a new message.",
        EMPTY_FILTER_VIEW: "Filtered View is Empty",
        EMPTY_FILTER_VIEW_TAGLINE: "Adjust or clear your filters to view conversations.",
        EMPTY_SAVED_VIEW: "Saved View is Empty.",
        EMPTY_SAVED_VIEW_TAGLINE: "Update your filters to view matching conversations.",
        EMPTY_UNREAD_FILTER_VIEW: "You're all caught up!",
        EMPTY_UNREAD_FILTER_VIEW_TAGLINE: "New unread messages will show up here.",
    },

    // General informational messages
    GENERAL: {
        INFO: "For your information.",
    },
};

// Helper function to get error message by key path
export const getErrorMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = ERROR_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Error message not found for key path: ${keyPath}`);
            return ERROR_MESSAGES.GENERAL.UNKNOWN_ERROR;
        }
    }

    return message;
};

// Helper function to get success message by key path
export const getSuccessMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = SUCCESS_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Success message not found for key path: ${keyPath}`);
            return SUCCESS_MESSAGES.GENERAL.OPERATION_SUCCESS;
        }
    }

    return message;
};

// Helper function to get confirmation message by key path
export const getConfirmationMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = CONFIRMATION_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Confirmation message not found for key path: ${keyPath}`);
            return CONFIRMATION_MESSAGES.GENERAL.DELETE_CONFIRMATION;
        }
    }

    return message;
};

// Helper function to get info message by key path
export const getInfoMessage = (keyPath) => {
    const keys = keyPath.split(".");
    let message = INFO_MESSAGES;

    for (const key of keys) {
        if (message && message[key]) {
            message = message[key];
        } else {
            console.warn(`Info message not found for key path: ${keyPath}`);
            return INFO_MESSAGES.GENERAL.INFO;
        }
    }

    return message;
};

// Export individual message categories for direct import
export const {
    TEMPLATE: TEMPLATE_ERRORS,
    VALIDATION,
    MESSAGE: MESSAGE_ERRORS,
    GENERAL: GENERAL_ERRORS,
    PHONE,
    USER: USER_ERRORS,
    TEAMS: TEAMS_ERRORS,
} = ERROR_MESSAGES;

export const {
    TEMPLATE: TEMPLATE_SUCCESS,
    MESSAGE: MESSAGE_SUCCESS,
    GENERAL: GENERAL_SUCCESS,
    USER: USER_SUCCESS,
    TEAMS: TEAMS_SUCCESS,
} = SUCCESS_MESSAGES;

export const { TEMPLATE: TEMPLATE_CONFIRMATION, GENERAL: GENERAL_CONFIRMATION } = CONFIRMATION_MESSAGES;
export const { TEMPLATE: TEMPLATE_INFO, GENERAL: GENERAL_INFO } = INFO_MESSAGES;
